/**
 * Cơ sở nào bán thương hiệu nào (bảng branch_brands, migration 0207).
 *
 * Trước bản này danh sách nằm ghi cứng ở hai chỗ trong mã; nay là dữ liệu, đọc
 * qua GET /branch-brands.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";

export const BRANCH_BRANDS_KEY = ["catalog", "branch-brands"] as const;

/** Mã cơ sở → danh sách mã thương hiệu đang bán. */
export type BrandsByBranch = Record<string, string[] | undefined>;

/** HẰNG SỐ: bên dùng đưa bản đồ vào dep của useEffect — trả `{}` mới mỗi lần
 *  gọi là effect chạy lại sau MỌI lần render. */
const EMPTY: BrandsByBranch = {};

export function useBranchBrands() {
  const q = useQuery({
    queryKey: BRANCH_BRANDS_KEY,
    queryFn: async (): Promise<BrandsByBranch> => {
      const { data, error } = await api.GET("/branch-brands");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được cơ sở bán thương hiệu nào"));
      return data.items;
    },
    staleTime: 5 * 60_000,
  });
  // Chưa về / lỗi → bản đồ RỖNG, và branchesSellingBrand hiểu rỗng là "chưa
  // biết" nên không lọc. Đó là điều muốn: mất mạng thì lễ tân vẫn tạo được đơn.
  return q.data ?? EMPTY;
}

/**
 * Lọc danh sách cơ sở theo thương hiệu đang chọn.
 *
 * Hai trường hợp CỐ Ý trả về tất cả, vì chúng khác hẳn nhau nhưng nếu lọc mù
 * thì cùng ra danh sách rỗng — mà rỗng trông y hệt "hệ thống hỏng":
 *   - chưa chọn thương hiệu (brandCode rỗng);
 *   - bản đồ chưa tải xong / API lỗi (map rỗng).
 * Còn "thương hiệu này chưa cơ sở nào bán" THÌ trả rỗng thật, để người dùng
 * thấy ngay chứ không lặng lẽ cho chọn một cơ sở không bán thương hiệu đó.
 */
export function branchesSellingBrand<T extends { value: string }>(
  branches: readonly T[],
  brandsByBranch: BrandsByBranch,
  brandCode: string,
): T[] {
  if (!brandCode) return [...branches];
  if (Object.keys(brandsByBranch).length === 0) return [...branches];
  return branches.filter((b) => (brandsByBranch[b.value] ?? []).includes(brandCode));
}

/**
 * Có phải XOÁ ô cơ sở vì cơ sở đang chọn không bán thương hiệu vừa chọn?
 *
 * Trả true CHỈ KHI biết chắc: có dòng khai báo cho đúng cơ sở đó mà thương
 * hiệu không nằm trong. Ba trạng thái dưới đây đều cho ra "danh sách rỗng" nếu
 * lọc mù, nhưng nghĩa khác hẳn nhau — gộp chúng là xoá mất lựa chọn của người
 * dùng mỗi lần mạng chậm (đã dính 02/09/2026):
 *   - chưa chọn cơ sở, hoặc chưa chọn gói  → chưa có gì để xét;
 *   - bản đồ chưa tải xong                 → CHƯA BIẾT, không được đụng vào;
 *   - cơ sở chưa khai thương hiệu nào      → CHƯA BIẾT, không được đụng vào.
 */
export function shouldClearBranch(
  branch: string,
  brandCode: string,
  brandsByBranch: BrandsByBranch,
): boolean {
  if (!branch || !brandCode) return false;
  const sold = brandsByBranch[branch];
  if (!sold) return false;
  return !sold.includes(brandCode);
}
